import { ScrollText } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrgScriptChoice } from "@/api/org-structure-conversation";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/**
 * A oferta do roteiro pronto, no lugar do console (lado direito) enquanto o consultor
 * não decide. A conversa só abre depois da escolha; o chat é só conversa, e esta
 * decisão não entra nele.
 *
 * Sim abre a unidade com o roteiro. Não pede confirmação num diálogo central, porque
 * recusar o roteiro gera custo extra de LLM numa unidade que já tem script pronto;
 * confirmado, a unidade abre com o agente.
 */
export function ScriptOfferPanel({
  unitType,
  code,
  version,
  onChoose,
}: {
  /** O tipo da unidade, já traduzido ("Company Code"). */
  unitType: string;
  /** O código efetivo da unidade ("7001"). */
  code: string;
  /** Versão do roteiro gravado; `null` quando o servidor não a informou. */
  version: number | null;
  onChoose: (choice: OrgScriptChoice) => void;
}) {
  const { t } = useTranslation();
  const [confirmingAgent, setConfirmingAgent] = useState(false);

  return (
    <div
      data-testid="script-offer-panel"
      className="flex h-full min-h-0 flex-col items-center justify-center gap-5 rounded-xl border border-[var(--exed-card-border)] bg-white px-6 py-8 text-center"
    >
      <span
        aria-hidden="true"
        className="flex h-12 w-12 items-center justify-center rounded-xl border border-[rgba(255,200,61,0.4)] bg-[rgba(255,200,61,0.14)] text-[#7a5600]"
      >
        <ScrollText className="h-6 w-6" />
      </span>
      <div className="flex max-w-[360px] flex-col gap-2">
        <h2 className="m-0 text-base font-semibold text-[#08254c]">
          {t("Ready script for {{type}} {{code}}", { type: unitType, code })}
        </h2>
        <p className="m-0 text-sm text-[#596b85]">
          {version === null
            ? t("A recorded script already registers this kind of unit without the agent: it is faster and costs nothing in LLM.")
            : t(
                "A recorded script (v{{version}}) already registers this kind of unit without the agent: it is faster and costs nothing in LLM.",
                { version },
              )}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button type="button" variant="action" onClick={() => onChoose("script")}>
          {t("Yes, run the script")}
        </Button>
        <Button type="button" variant="outline" onClick={() => setConfirmingAgent(true)}>
          {t("No, continue with the agent")}
        </Button>
      </div>
      <AlertDialog open={confirmingAgent} onOpenChange={setConfirmingAgent}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("Continue with the agent?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "The agent works with the language model, which adds LLM cost to this unit, and a ready script already exists for it. Continue only if you want the conversation.",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("Back")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => onChoose("llm")}>{t("Continue with the agent")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
